import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash.omit';
import ReactTooltip from 'react-tooltip';

import BorderIcon from '../../../../../../../icons/BorderIcon';
import RangeSlider from '../../../../../../RangeSlider';
import Input from '../../../../../../Input';
import Select from '../../../../../../Select';
import { CARD_BORDER_TYPE } from '../../../../../../../../services/cards/constants';
import { capitalize } from '../../../../../../../../util/text';
import CardDesignerCardElementColorControl from '../CardDesignerCardElementColorControl';
import DropdownMenu from '../../../../../../DropdownMenu';

const propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string,
    cardElementConfiguration: PropTypes.shape({
      corner_radius: PropTypes.number,
      border: PropTypes.shape({
        width: PropTypes.number,
        type: PropTypes.oneOf(Object.values(CARD_BORDER_TYPE)),
        color: PropTypes.string,
        opacity: PropTypes.number,
      }),
    }),
  }).isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
};

const CardDesignerCardElementBorderControl = ({
  element,
  onElementConfigurationChange,
}) => {
  const { cardElementConfiguration } = element;
  const { corner_radius: elementBorderRadius, border: elementBorder } =
    cardElementConfiguration ?? {};

  // We need a base config since, otherwise, the border will fail
  // initially when the user tries something (i.e add a border).
  const baseBorderConfig = {
    type: CARD_BORDER_TYPE.SOLID,
    width: 0,
    color: '#000',
    opacity: 1,
  };

  const [borderWidth, setBorderWidth] = useState(elementBorder?.width ?? 0);
  const [borderRadius, setBorderRadius] = useState(elementBorderRadius ?? 0);

  useEffect(() => {
    setBorderRadius(elementBorderRadius ?? 0);
  }, [elementBorderRadius]);

  useEffect(() => {
    setBorderWidth(elementBorder?.width ?? 0);
  }, [elementBorder?.width]);

  return (
    <DropdownMenu
      placement="bottom-start"
      dropdownClassName="card-designer-card-element-common-control-dropdown"
      toggleClassName="ml-1 d-flex align-items-center"
      toggle={
        <span
          data-for="border-controls"
          data-place="bottom"
          data-effect="solid"
          data-tip
        >
          <BorderIcon size={22} />
          <ReactTooltip id="border-controls" className="tooltip">
            Border Controls
          </ReactTooltip>
        </span>
      }
    >
      <div className="p-2 d-flex w-100 align-items-center ml-auto text-sm text-nowrap">
        <span className="mr-1">Border width</span>
        <RangeSlider
          className="mx-2"
          onChange={values => {
            setBorderWidth(Math.round((values[0] / 1e2) * 1e2) / 1e2);
          }}
          onFinalChange={values => {
            onElementConfigurationChange({
              element,
              config: {
                border: {
                  ...baseBorderConfig,
                  ...omit(elementBorder, ['__typename']),
                  width: Math.round((values[0] / 1e2) * 1e2) / 1e2,
                },
              },
            });
          }}
          values={[Math.round(borderWidth * 1e2)]}
          min={0}
          max={100}
        />
        <Input
          id="border-width"
          type="number"
          max={100}
          min={0}
          value={Math.round(borderWidth * 1e2)}
          onChange={({ target }) => {
            const percentValue = Math.round((target.value / 1e2) * 1e2) / 1e2;
            setBorderWidth(percentValue);
            onElementConfigurationChange({
              element,
              config: {
                border: {
                  ...baseBorderConfig,
                  ...omit(elementBorder, ['__typename']),
                  width: percentValue,
                },
              },
            });
          }}
        />
      </div>
      <div className="p-2 d-flex w-100 align-items-center ml-auto text-sm text-nowrap">
        <span className="mr-1">Border radius</span>
        <RangeSlider
          className="mx-2"
          onChange={values =>
            setBorderRadius(Math.round((values[0] / 1e2) * 1e2) / 1e2)
          }
          onFinalChange={values =>
            onElementConfigurationChange({
              element,
              config: {
                corner_radius: Math.round((values[0] / 1e2) * 1e2) / 1e2,
              },
            })
          }
          values={[Math.round(borderRadius * 1e2)]}
          min={0}
          max={100}
        />
        <Input
          id="border-radius"
          value={Math.round(borderRadius * 1e2)}
          type="number"
          max={100}
          min={0}
          onChange={({ target }) => {
            const percentValue = Math.round((target.value / 1e2) * 1e2) / 1e2;
            setBorderRadius(percentValue);
            onElementConfigurationChange({
              element,
              config: {
                corner_radius: percentValue,
              },
            });
          }}
        />
      </div>
      <hr className="my-0" />
      <div className="p-2 d-flex w-100 align-items-center ml-auto text-sm text-nowrap">
        Border Style
        <Select
          id="text-align"
          size="sm"
          className="w-100 mb-0 ml-2"
          value={elementBorder?.type || CARD_BORDER_TYPE.SOLID}
          onChange={event => {
            const { value } = event.target;
            onElementConfigurationChange({
              element,
              config: {
                border: {
                  ...baseBorderConfig,
                  ...omit(elementBorder, ['__typename']),
                  type: value,
                },
              },
            });
          }}
        >
          {Object.values(CARD_BORDER_TYPE).map(borderType => {
            return (
              <option key={borderType} value={borderType}>
                {capitalize(borderType)}
              </option>
            );
          })}
        </Select>
      </div>
      <div className="p-2 d-flex w-100 align-items-center ml-auto text-sm text-nowrap">
        Border Color
        <CardDesignerCardElementColorControl
          className="ml-auto"
          onChange={hex => {
            onElementConfigurationChange({
              element,
              config: {
                border: {
                  ...baseBorderConfig,
                  ...omit(elementBorder, ['__typename']),
                  color: hex,
                },
              },
            });
          }}
          defaultColor={elementBorder?.color ?? undefined}
        />
      </div>
    </DropdownMenu>
  );
};

CardDesignerCardElementBorderControl.propTypes = propTypes;

export default CardDesignerCardElementBorderControl;
